// components/exam/FeedbackDashboard.tsx
import React, { useState } from 'react';
import MarkdownRenderer from '../common/MarkdownRenderer';
import '../../styles/components/FeedbackDashboard.css';

interface FeedbackDashboardProps {
  performance: {
    score: number;
    percentage: number;
    readinessScore: number;
    predictedOutcome: "Fail Risk" | "Borderline" | "Likely Pass";
  };
  markingBreakdown?: Array<{
    criteria: string;
    maxScore: number;
    scoreAwarded: number;
    examinerExpectation: string;
    userPerformance: string;
    marksLostReason: string;
  }>;
  cognitiveAnalysis?: {
    structure: number;
    clarity: number;
    conceptDepth: number;
    blankRisk: number;
  };
  missingPoints?: string[];
  strongPoints?: string[];
  examinerComment?: string;
  studyPlan?: {
    focusAreas: string[];
    actions: string[];
    estimatedImprovement: string;
  };
  retestPlan?: {
    recommended: boolean;
    instructions: string;
  };
  questions: any[];
  answers: any[];
  theme: 'light' | 'dark';
  onTakeAnother: () => void;
  onSeeCorrectAnswers: () => void;
  onChat: () => void;
}

const FeedbackDashboard: React.FC<FeedbackDashboardProps> = ({
  performance,
  markingBreakdown,
  cognitiveAnalysis,
  missingPoints,
  studyPlan,
  retestPlan,
  questions,
  answers,
  theme,
  onTakeAnother,
  onSeeCorrectAnswers,
  onChat
}) => {
  const [showDetailedBreakdown, setShowDetailedBreakdown] = useState(false);
  const [showModelAnswers, setShowModelAnswers] = useState(false);

  const getReadinessColor = (score: number) => {
    if (score >= 70) return '#10b981';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
  };

  const getPredictedOutcomeIcon = (outcome: string) => {
    switch(outcome) {
      case 'Likely Pass': return '✅';
      case 'Borderline': return '⚠️';
      default: return '❌';
    }
  };

  // Extract structured key insights from missing points
  const getKeyInsights = () => {
    if (!missingPoints || missingPoints.length === 0) return [];
    
    const insights: {
      topic: string;
      issue: string;
      mistake: string;
      impact: string;
    }[] = [];
    
    missingPoints.forEach(point => {
      // Parse the point to extract structured info
      let topic = point;
      let issue = '';
      let mistake = '';
      let impact = '';
      
      // Extract mark loss
      const markMatch = point.match(/\([-\d]+\s*mark[s]?\)/i);
      const marks = markMatch ? markMatch[0] : '';
      
      // Clean topic
      topic = point.replace(markMatch?.[0] || '', '').trim();
      topic = topic.replace(/^[•\-*]\s*/, '').trim();
      
      // Determine issue type based on keywords
      if (topic.toLowerCase().includes('explanation') || topic.toLowerCase().includes('explain')) {
        issue = 'Too much explanation / Not precise';
        mistake = 'Lacked focus on key points';
        impact = 'Lost marks for verbosity';
      } else if (topic.toLowerCase().includes('missing') || topic.toLowerCase().includes('omitted')) {
        issue = 'Incomplete response';
        mistake = 'Key points not covered';
        impact = marks || 'Partial credit only';
      } else if (topic.toLowerCase().includes('structure') || topic.toLowerCase().includes('organization')) {
        issue = 'Poor structure';
        mistake = 'Answer lacks clear flow';
        impact = 'Marks lost for organization';
      } else if (topic.toLowerCase().includes('example') || topic.toLowerCase().includes('application')) {
        issue = 'No real-world example';
        mistake = 'Theory without application';
        impact = 'Lost marks for practical connection';
      } else if (topic.toLowerCase().includes('calculation') || topic.toLowerCase().includes('formula')) {
        issue = 'Calculation error';
        mistake = 'Incorrect formula application';
        impact = marks || 'Marks lost';
      } else if (topic.toLowerCase().includes('depth') || topic.toLowerCase().includes('detailed')) {
        issue = 'Surface-level answer';
        mistake = 'Needs deeper explanation';
        impact = 'Lost marks for shallow coverage';
      } else {
        issue = 'Key point missed';
        mistake = topic;
        impact = marks || 'Credit lost';
      }
      
      insights.push({
        topic: topic.split(':')[0].substring(0, 50),
        issue,
        mistake,
        impact
      });
    });
    
    return insights.slice(0, 4); // Top 4 insights only
  };

  // Get pattern insight
  const getPatternInsight = () => {
    if (!missingPoints || missingPoints.length === 0) return null;
    
    const patterns: string[] = [];
    let mainIssue = '';
    
    // Analyze missing points for patterns
    const hasExplanationIssue = missingPoints.some(p => 
      p.toLowerCase().includes('explanation') || 
      p.toLowerCase().includes('detail') ||
      p.toLowerCase().includes('depth')
    );
    
    const hasStructureIssue = missingPoints.some(p => 
      p.toLowerCase().includes('structure') || 
      p.toLowerCase().includes('organiz') ||
      p.toLowerCase().includes('flow')
    );
    
    const hasExampleIssue = missingPoints.some(p => 
      p.toLowerCase().includes('example') || 
      p.toLowerCase().includes('application')
    );
    
    const hasMissingIssue = missingPoints.some(p => 
      p.toLowerCase().includes('missing') || 
      p.toLowerCase().includes('omitted') ||
      p.toLowerCase().includes('incomplete')
    );
    
    if (hasExplanationIssue) patterns.push('depth of explanation');
    if (hasStructureIssue) patterns.push('answer structure');
    if (hasExampleIssue) patterns.push('real-world examples');
    if (hasMissingIssue) patterns.push('completeness');
    
    if (patterns.length > 0) {
      mainIssue = patterns.join(' & ');
    }
    
    if (mainIssue) {
      return {
        issue: mainIssue,
        message: `You know the content, but lose marks on ${mainIssue}.`
      };
    }
    
    return null;
  };

  // Simplify marking breakdown for collapsible view
  const getSimpleMarkingGuide = () => {
    if (!markingBreakdown || markingBreakdown.length === 0) return [];
    
    return markingBreakdown.map(item => ({
      criteria: item.criteria,
      score: `${item.scoreAwarded}/${item.maxScore}`,
      reason: item.marksLostReason,
      isFull: item.scoreAwarded === item.maxScore,
      isPartial: item.scoreAwarded > 0 && item.scoreAwarded < item.maxScore,
      isNone: item.scoreAwarded === 0
    }));
  };

  const patternInsight = getPatternInsight();
  const keyInsights = getKeyInsights();
  const simpleMarkingGuide = getSimpleMarkingGuide();

  // Calculate expected improvement
  const getExpectedImprovement = () => {
    if (studyPlan?.estimatedImprovement) {
      const match = studyPlan.estimatedImprovement.match(/\d+/);
      if (match) {
        const gain = parseInt(match[0]);
        return {
          current: performance.percentage,
          expected: Math.min(100, performance.percentage + gain),
          gain
        };
      }
    }
    const gain = Math.min(30, Math.max(10, 100 - performance.readinessScore));
    return {
      current: performance.percentage,
      expected: Math.min(100, performance.percentage + gain),
      gain
    };
  };

  const improvement = getExpectedImprovement();

  return (
    <div className={`feedback-dashboard ${theme}`}>
      {/* 1. TOP SUMMARY CHARTS */}
      <div className="dashboard-header">
        <h2>📊 Exam Performance Summary</h2>
        <div className="score-summary-cards">
          <div className="score-card">
            <div className="score-label">Total Score</div>
            <div className="score-value">{performance.score}/{questions.reduce((sum, q) => sum + q.maxMarks, 0)}</div>
            <div className="score-percentage">{performance.percentage}%</div>
            <div className="score-progress">
              <div className="progress-bar" style={{ width: `${performance.percentage}%`, backgroundColor: getReadinessColor(performance.percentage) }}></div>
            </div>
          </div>
          
          <div className="readiness-card">
            <div className="readiness-label">Readiness Score</div>
            <div className="readiness-value" style={{ color: getReadinessColor(performance.readinessScore) }}>
              {performance.readinessScore}%
            </div>
            <div className="readiness-progress">
              <div className="progress-bar" style={{ width: `${performance.readinessScore}%`, backgroundColor: getReadinessColor(performance.readinessScore) }}></div>
            </div>
            <div className="predicted-outcome">
              {getPredictedOutcomeIcon(performance.predictedOutcome)} {performance.predictedOutcome}
            </div>
          </div>
        </div>
      </div>

      {/* Cognitive Analysis Charts */}
      {cognitiveAnalysis && (
        <div className="cognitive-analysis-section">
          <h3>🧠 Cognitive Performance Analysis</h3>
          <div className="cognitive-metrics">
            <div className="metric-item">
              <div className="metric-header">
                <span>📐 Structure</span>
                <span>{cognitiveAnalysis.structure}%</span>
              </div>
              <div className="metric-progress">
                <div className="progress-fill" style={{ width: `${cognitiveAnalysis.structure}%`, backgroundColor: getReadinessColor(cognitiveAnalysis.structure) }}></div>
              </div>
            </div>
            <div className="metric-item">
              <div className="metric-header">
                <span>💡 Clarity</span>
                <span>{cognitiveAnalysis.clarity}%</span>
              </div>
              <div className="metric-progress">
                <div className="progress-fill" style={{ width: `${cognitiveAnalysis.clarity}%`, backgroundColor: getReadinessColor(cognitiveAnalysis.clarity) }}></div>
              </div>
            </div>
            <div className="metric-item">
              <div className="metric-header">
                <span>🎯 Concept Depth</span>
                <span>{cognitiveAnalysis.conceptDepth}%</span>
              </div>
              <div className="metric-progress">
                <div className="progress-fill" style={{ width: `${cognitiveAnalysis.conceptDepth}%`, backgroundColor: getReadinessColor(cognitiveAnalysis.conceptDepth) }}></div>
              </div>
            </div>
            <div className="metric-item">
              <div className="metric-header">
                <span>⚠️ Blank Risk</span>
                <span>{cognitiveAnalysis.blankRisk}%</span>
              </div>
              <div className="metric-progress">
                <div className="progress-fill" style={{ width: `${cognitiveAnalysis.blankRisk}%`, backgroundColor: cognitiveAnalysis.blankRisk > 50 ? '#ef4444' : '#f59e0b' }}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2. KEY INSIGHTS - Structured, precise */}
      {keyInsights.length > 0 && (
        <div className="key-insights-section">
          <div className="insights-header">
            <span className="insights-icon">🔍</span>
            <h3>Key Insights</h3>
          </div>
          <div className="insights-grid">
            {keyInsights.map((insight, idx) => (
              <div key={idx} className="insight-card">
                <div className="insight-topic">{insight.topic}</div>
                <div className="insight-details">
                  <div className="insight-row">
                    <span className="insight-label">Issue:</span>
                    <span className="insight-value">{insight.issue}</span>
                  </div>
                  <div className="insight-row">
                    <span className="insight-label">Mistake:</span>
                    <span className="insight-value">{insight.mistake}</span>
                  </div>
                  <div className="insight-impact">{insight.impact}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3. WHAT TO FIX - Short action items */}
      {studyPlan && studyPlan.actions && studyPlan.actions.length > 0 && (
        <div className="action-highlights">
          <div className="action-header">
            <span className="action-icon">🎯</span>
            <h3>What to Fix</h3>
          </div>
          <div className="action-list">
            {studyPlan.actions.slice(0, 4).map((action, idx) => (
              <div key={idx} className="action-item">
                <span className="action-number">{idx + 1}.</span>
                <span className="action-text">{action}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 4. PATTERN INSIGHT */}
      {patternInsight && (
        <div className="pattern-insight">
          <div className="pattern-header">
            <span className="pattern-icon">🧠</span>
            <span className="pattern-badge">Pattern Detected</span>
          </div>
          <div className="pattern-content">
            <p>{patternInsight.message}</p>
            <p className="pattern-impact">👉 This is why your score is at {performance.percentage}%</p>
          </div>
        </div>
      )}

      {/* 5. IMPROVEMENT PROJECTION */}
      <div className="improvement-projection">
        <div className="projection-header">
          <span className="projection-icon">📈</span>
          <span className="projection-title">If You Fix These Areas</span>
        </div>
        <div className="projection-values">
          <div className="current-score">
            <span className="label">Current</span>
            <span className="value">{improvement.current}%</span>
          </div>
          <div className="projection-arrow">→</div>
          <div className="target-score">
            <span className="label">Expected</span>
            <span className="value highlight">+{improvement.gain}%</span>
            <span className="value">{improvement.expected}%</span>
          </div>
        </div>
        <div className="projection-progress">
          <div className="progress-bar" style={{ width: `${improvement.current}%`, backgroundColor: getReadinessColor(improvement.current) }}></div>
          <div className="progress-target" style={{ left: `${improvement.expected}%` }}></div>
        </div>
      </div>

      {/* 6. RETEST CTA */}
      {retestPlan && (
        <div className={`retest-cta ${retestPlan.recommended ? 'recommended' : 'completed'}`}>
          <div className="cta-content">
            <span className="cta-icon">{retestPlan.recommended ? '🔄' : '🎉'}</span>
            <span className="cta-text">
              {retestPlan.recommended ? 'Fix These Weak Areas Now' : 'Ready for the Real Exam!'}
            </span>
          </div>
          <button className="cta-button" onClick={onTakeAnother}>
            {retestPlan.recommended ? 'Take Retest' : 'Practice More'}
          </button>
        </div>
      )}

      {/* 7. OPTIONAL DETAILS - Simplified Marking Guide */}
      <div className="optional-details">
        <button 
          className={`collapse-toggle ${showDetailedBreakdown ? 'open' : ''}`}
          onClick={() => setShowDetailedBreakdown(!showDetailedBreakdown)}
        >
          <span className="toggle-icon">▼</span>
          <span className="toggle-text">View Marking Guide</span>
        </button>
        
        {showDetailedBreakdown && simpleMarkingGuide.length > 0 && (
          <div className="marking-guide">
            <div className="guide-header">
              <div className="guide-col-criteria">Criteria</div>
              <div className="guide-col-score">Score</div>
              <div className="guide-col-reason">Why Marks Lost</div>
            </div>
            {simpleMarkingGuide.map((item, idx) => (
              <div key={idx} className="guide-row">
                <div className="guide-col-criteria">{item.criteria}</div>
                <div className="guide-col-score">
                  <span className={`score-badge ${item.isFull ? 'full' : item.isPartial ? 'partial' : 'none'}`}>
                    {item.score}
                  </span>
                </div>
                <div className="guide-col-reason">{item.reason || '-'}</div>
              </div>
            ))}
          </div>
        )}

        <button 
          className={`collapse-toggle ${showModelAnswers ? 'open' : ''}`}
          onClick={() => setShowModelAnswers(!showModelAnswers)}
        >
          <span className="toggle-icon">▼</span>
          <span className="toggle-text">View Model Answers</span>
        </button>
        
        {showModelAnswers && questions && questions.length > 0 && (
          <div className="model-answers">
            {questions.map((q, idx) => (
              <div key={idx} className="model-answer-item">
                <div className="model-question">
                  <strong>Q{idx + 1}:</strong> <MarkdownRenderer content={q.question} theme={theme} />
                </div>
                <div className="model-answer-content">
                  <strong>Model Answer:</strong>
                  <MarkdownRenderer content={q.modelAnswer} theme={theme} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="feedback-actions">
        <button className="action-btn take-another" onClick={onTakeAnother}>
          📝 Take Another Practice
        </button>
        <button className="action-btn chat" onClick={onChat}>
          💬 Ask AI Tutor
        </button>
      </div>
    </div>
  );
};

export default FeedbackDashboard;